import React, { Suspense } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
// Tokens de "Tinta y papel" ANTES de style.css: son las custom properties de
// las que cuelga todo lo demás, y la cascada exige que estén declaradas antes
// de que alguien las use. Hasta ahora sólo existían dentro del shell móvil
// (mobile.css, cargado por el chunk lazy de MobileApp), así que en el documento
// de escritorio los --mt-* sencillamente no existían.
import './generated/tokens.css';
import './style.css';
// Capa de contratos de "Tinta y papel" para la web de escritorio (.ds-*).
// Va DESPUÉS de style.css a propósito: declara las cajas del sistema
// (barra de acciones, botones, tabs, tabla, campo, capas, estados) y, a
// igualdad de especificidad, la cascada debe resolver a favor del sistema
// y no de la regla histórica de la pantalla que lo contradiga.
import './design-system-web.css';
import './i18n';
import { runFpsWatchdog } from "./utils/perfMode";
import { captureFbclid } from "./utils/metaPixel";
import { isMobileHost, redirectPhoneToMobileHost } from "./utils/mobileHost";

// Shell móvil (m.appeva.app): chunk aparte vía lazy para que el escritorio no
// cargue ni un byte de él.
const MobileApp = React.lazy(() => import("./mobile/MobileApp"));

// Persist the Meta click-id (fbclid → _fbc) as early as possible so both the
// Pixel and the Conversions API have a click identifier even after the URL
// param is dropped on SPA navigation.
captureFbclid();

// Mide los FPS reales tras la carga (con retraso, para no contar el render
// inicial) y degrada al tema lite si detecta jank sostenido en modo automático.
if (typeof window !== 'undefined') {
  const start = () => setTimeout(runFpsWatchdog, 1500);
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
}

// ── Sentry Error Tracking ────────────────────────────────────────────────────
const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: process.env.NODE_ENV || 'production',
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.5,
    beforeSend(event) {
      // Strip PII from URLs
      if (event.request?.url) {
        event.request.url = event.request.url.replace(/token=[^&]+/, 'token=REDACTED');
      }
      return event;
    },
    ignoreErrors: [
      'ResizeObserver loop',
      'Non-Error promise rejection',
      'Loading chunk',
      'Network request failed',
    ],
  });
}

const container = document.getElementById("root") as HTMLElement;
// Teléfono en el dominio web sobre una ruta de la app → salta a m.appeva.app.
// Si redirige, no se monta nada: el spinner de arranque cubre el viaje.
const redirecting = redirectPhoneToMobileHost();
const mobileShell = !redirecting && isMobileHost();
const wrap = (children: React.ReactNode) => (
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div style={{
          minHeight: '100vh', display: 'flex', alignItems: 'center',
          justifyContent: 'center', background: '#07111f', color: '#fff',
          padding: 24, textAlign: 'center'
        }}>
          <div>
            <h2 style={{ marginBottom: 12 }}>Algo salió mal</h2>
            <p style={{ color: '#94a3b8', marginBottom: 20 }}>
              Ocurrió un error inesperado. El equipo de Eva ya fue notificado.
            </p>
            <button
              onClick={() => { resetError(); window.location.href = '/'; }}
              style={{
                padding: '10px 24px', background: '#6978ff', color: '#fff',
                border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600
              }}
            >
              Volver al inicio
            </button>
          </div>
        </div>
      )}
    >
      <Suspense
        fallback={
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <div className="spinner" />
          </div>
        }
      >
        {children}
      </Suspense>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

// react-snap prerenders these routes to static HTML at build time. When that
// markup is present we hydrate over it; otherwise we render fresh (dev / SPA).
// __evaBooting: en rutas de la app (no prerenderizadas) el snippet de index.html
// ya reemplazó el snapshot de la landing por un spinner — ahí NO hidratamos
// (el markup no corresponde a la ruta); montamos fresco con createRoot.
// En el shell móvil nunca hay markup válido (los snapshots son de escritorio).
if (redirecting) {
  // location.replace ya está en curso; montar React solo haría parpadear.
} else if (mobileShell) {
  // El shell móvil no hidrata nunca (los snapshots de react-snap son de
  // escritorio) y, sobre todo, NO importa `./App`: así el teléfono deja de
  // descargar el panel de escritorio entero, que era el grueso del bundle.
  createRoot(container).render(wrap(<MobileApp />));
} else {
  // Escritorio: se espera al chunk de App ANTES de montar. Con `lazy` el primer
  // render sería el spinner del Suspense y rompería la hidratación del markup
  // prerenderizado por react-snap.
  void import("./App").then(({ default: App }) => {
    const tree = wrap(<App />);
    if (container.hasChildNodes() && !(window as any).__evaBooting) {
      hydrateRoot(container, tree);
    } else {
      createRoot(container).render(tree);
    }
  });
}

// ── Service Worker Registration ──────────────────────────────────────────────
function getPageAssetUrls(): string[] {
  const urls: string[] = [];
  // JS bundles
  document.querySelectorAll('script[src]').forEach((el) => {
    const src = (el as HTMLScriptElement).src;
    if (src && src.startsWith(window.location.origin)) urls.push(src);
  });
  // CSS bundles
  document.querySelectorAll('link[rel="stylesheet"][href]').forEach((el) => {
    const href = (el as HTMLLinkElement).href;
    if (href && href.startsWith(window.location.origin)) urls.push(href);
  });
  return urls;
}

if ('serviceWorker' in navigator && process.env.NODE_ENV !== 'production') {
  // En desarrollo el SW es veneno: sus assets van cacheFirst sin revalidar y
  // sirven código VIEJO por encima del hot reload ("no veo mis cambios").
  // Aquí no solo no se registra: se desregistra el que haya quedado de un
  // build de producción en este mismo origen y se vacían sus cachés.
  navigator.serviceWorker.getRegistrations().then((rs) => rs.forEach((r) => { void r.unregister(); }));
  if ('caches' in window) {
    caches.keys().then((keys) => keys.forEach((k) => { void caches.delete(k); }));
  }
}

if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        console.log('[SW] Registered:', registration.scope);
        setInterval(() => registration.update(), 60 * 60 * 1000);

        // Once the SW is active, send current page assets so they get cached
        // for offline reload support.
        const sendAssets = () => {
          if (navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({
              type: 'CACHE_APP_ASSETS',
              urls: getPageAssetUrls(),
            });
          }
        };

        if (navigator.serviceWorker.controller) {
          sendAssets();
        } else {
          // SW just installed — wait for it to claim this client
          navigator.serviceWorker.addEventListener('controllerchange', sendAssets, { once: true });
        }
      })
      .catch((err) => {
        console.warn('[SW] Registration failed:', err);
      });
  });
}
